import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatFecha, formatFechaHora } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { ShareCarouselDialog } from "@/components/ShareCarouselDialog";
import { ReportButton } from "@/components/ReportButton";
import { toast } from "sonner";
import {
  Heart, CalendarDays, ArrowUp, BookOpenText, HandHeart, ListChecks,
  MessageCircleHeart, Sparkles, Quote, Megaphone, Trash2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Parrafos = ({ texto }) => (
  <div className="reading-body space-y-4 sm:space-y-5">
    {(texto || "").split("\n\n").map((p, i) => (
      <p key={i} className="text-base text-foreground/90">{p}</p>
    ))}
  </div>
);

export default function MensajeDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [message, setMessage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [sending, setSending] = useState(false);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/messages/${id}`)
      .then((res) => setMessage(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get(`/messages/${id}/comments`).then((res) => setComments(res.data.comments || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) {
      api.get("/me/favorites/ids").then((res) => setIsFavorite((res.data.ids || []).includes(id))).catch(() => {});
    }
  }, [user, id]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > window.innerHeight * 0.35);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleFavorite = useCallback(async () => {
    if (!user) {
      toast.info("Inicia sesión para guardar mensajes en favoritos");
      navigate("/login");
      return;
    }
    try {
      if (isFavorite) {
        await api.delete(`/me/favorites/${id}`);
        setIsFavorite(false);
        toast.success("Se quitó de tus favoritos");
      } else {
        await api.post(`/me/favorites/${id}`);
        setIsFavorite(true);
        toast.success("Guardado en Favoritos");
      }
    } catch {
      toast.error("No se pudo actualizar. Intenta de nuevo.");
    }
  }, [user, isFavorite, id, navigate]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || commentText.trim().length < 2) return;
    setSending(true);
    try {
      const res = await api.post(`/messages/${id}/comments`, { texto: commentText.trim() });
      setComments((prev) => [res.data, ...prev]);
      setCommentText("");
      toast.success("Gracias por compartir tu palabra de ánimo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo publicar el comentario");
    } finally {
      setSending(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success("Comentario eliminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar el comentario");
    }
  };

  const scrollToComments = () => {
    document.querySelector('[data-testid="message-comments-section"]')?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[42rem] px-4 py-12 sm:px-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="mt-4 h-5 w-1/3" />
        <Skeleton className="mt-8 h-64 w-full" />
      </div>
    );
  }

  if (notFound || !message) {
    return (
      <div className="mx-auto w-full max-w-[42rem] px-4 py-20 text-center sm:px-6" data-testid="message-not-found">
        <p className="font-serif text-2xl font-semibold">Mensaje no encontrado</p>
        <p className="mt-2 text-sm text-muted-foreground">Es posible que haya sido movido o aún no esté publicado.</p>
        <Button className="mt-6" onClick={() => navigate("/archivo")}>Ir al archivo</Button>
      </div>
    );
  }

  const isNewStructure = !!message.conexion;

  return (
    <div>
      <div className="gradient-breeze">
        <div className="mx-auto w-full max-w-[46rem] px-4 py-10 sm:px-6 sm:py-14">
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4" /> {formatFecha(message.fecha)}
            </span>
            {(message.tags || []).map((tag) => (
              <Badge key={tag} variant="secondary" className="bg-accent text-accent-foreground">{tag}</Badge>
            ))}
          </div>
          <h1 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl" data-testid="message-detail-title">
            {message.titulo}
          </h1>
          <p className="mt-3 text-base font-semibold text-primary" data-testid="message-detail-pasaje">{message.pasaje}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              variant={isFavorite ? "secondary" : "outline"}
              className="bg-card"
              onClick={toggleFavorite}
              data-testid="message-detail-favorite-toggle"
            >
              <Heart className={`mr-2 h-4 w-4 ${isFavorite ? "fill-destructive text-destructive" : ""}`} />
              {isFavorite ? "En tus favoritos" : "Guardar en favoritos"}
            </Button>
            <ShareCarouselDialog message={message} />
          </div>
        </div>
      </div>

      <article className="mx-auto w-full max-w-[46rem] px-4 py-10 sm:px-6">
        {/* Imagen del Dia */}
        {message.has_image && (
          <figure className="mb-10 overflow-hidden rounded-[var(--radius)] border border-border shadow-[var(--shadow-md)]" data-testid="message-imagen-dia">
            <img
              src={`${BACKEND_URL}/api/messages/${message.id}/imagen?v=${message.imagen_version || 1}`}
              alt={`Imagen del día: ${message.titulo}`}
              className="w-full object-cover"
            />
            <figcaption className="bg-muted/60 px-4 py-2 text-center text-xs text-muted-foreground">
              Imagen del día · compártela con alguien que la necesite
            </figcaption>
          </figure>
        )}

        {/* Versiculo clave */}
        <section className="rounded-[var(--radius)] border border-border bg-secondary/50 p-6" data-testid="message-section-texto-biblico">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
            <BookOpenText className="h-4 w-4" /> Versículo clave
          </div>
          <blockquote className="font-scripture border-l-2 border-primary/40 pl-4 text-lg italic leading-relaxed text-foreground/90">
            {message.texto_biblico}
          </blockquote>
          <p className="mt-3 text-sm font-semibold text-muted-foreground">— {message.pasaje}</p>
        </section>

        {isNewStructure ? (
          <>
            {/* Conexion empatica */}
            <section className="mt-10" data-testid="message-section-conexion">
              <Parrafos texto={message.conexion} />
            </section>

            <Separator className="my-10" />

            {/* Tres puntos clave */}
            {(message.puntos_clave || []).length > 0 && (
              <section data-testid="message-section-puntos">
                <div className="mb-6 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h2 className="font-serif text-2xl font-semibold">Tres claves para hoy</h2>
                </div>
                <div className="space-y-6">
                  {message.puntos_clave.map((punto, i) => (
                    <div key={i} className="rounded-[var(--radius)] border border-border bg-card p-6 shadow-[var(--shadow-sm)]" data-testid={`message-punto-${i + 1}`}>
                      <div className="mb-3 flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                          {i + 1}
                        </span>
                        <h3 className="font-serif text-xl font-semibold text-primary">{punto.titulo}</h3>
                      </div>
                      <Parrafos texto={punto.contenido} />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Verdad para meditar */}
            {message.verdad_destacada && (
              <section className="mt-10" data-testid="message-section-verdad">
                <div className="relative overflow-hidden rounded-[var(--radius)] brand-footer p-8 text-center sm:p-10">
                  <Quote className="mx-auto mb-4 h-8 w-8 text-[#00CCFF]" />
                  <p className="font-scripture text-xl italic leading-relaxed text-white sm:text-2xl">
                    “{message.verdad_destacada}”
                  </p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-white/70">
                    Verdad para meditar hoy
                  </p>
                </div>
              </section>
            )}

            {/* Oracion de entrega */}
            <section className="mt-10 rounded-[var(--radius)] border border-border bg-accent/40 p-6" data-testid="message-section-oracion">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                <HandHeart className="h-4 w-4" /> Oración de entrega
              </div>
              <p className="font-scripture text-lg italic leading-relaxed text-foreground/90">{message.oracion}</p>
            </section>

            {/* Llamado a la accion digital */}
            {message.llamado_accion && (
              <section className="mt-10 rounded-[var(--radius)] border-2 border-dashed border-primary/40 bg-secondary/40 p-6" data-testid="message-section-cta">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                  <Megaphone className="h-4 w-4" /> Hazlo hoy
                </div>
                <p className="text-base leading-relaxed text-foreground/90">{message.llamado_accion}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <ShareCarouselDialog message={message} />
                  <Button variant="outline" className="bg-card" onClick={scrollToComments} data-testid="cta-comentar-button">
                    <MessageCircleHeart className="mr-2 h-4 w-4" /> Comentar
                  </Button>
                  <Button variant="outline" className="bg-card" onClick={() => navigate("/oracion")} data-testid="cta-oracion-button">
                    <HandHeart className="mr-2 h-4 w-4" /> Muro de Oración
                  </Button>
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            {/* Estructura anterior (compatibilidad) */}
            <section className="mt-10" data-testid="message-section-mensaje">
              <h2 className="mb-4 font-serif text-2xl font-semibold">Mensaje</h2>
              <Parrafos texto={message.mensaje} />
            </section>
            {message.analisis && (
              <>
                <Separator className="my-10" />
                <section data-testid="message-section-analisis">
                  <div className="mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h2 className="font-serif text-2xl font-semibold">Análisis bíblico</h2>
                  </div>
                  <Parrafos texto={message.analisis} />
                </section>
              </>
            )}
            <section className="mt-10 rounded-[var(--radius)] border border-border bg-accent/40 p-6" data-testid="message-section-oracion">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                <HandHeart className="h-4 w-4" /> Oración
              </div>
              <p className="font-scripture text-lg italic leading-relaxed text-foreground/90">{message.oracion}</p>
            </section>
            {(message.acciones_practicas || []).length > 0 && (
              <section className="mt-10" data-testid="message-section-acciones">
                <div className="mb-4 flex items-center gap-2">
                  <ListChecks className="h-5 w-5 text-primary" />
                  <h2 className="font-serif text-2xl font-semibold">Acciones para hoy</h2>
                </div>
                <ul className="space-y-3">
                  {message.acciones_practicas.map((a, i) => (
                    <li key={i} className="flex gap-3 rounded-[var(--radius)] border border-border bg-card p-4 text-sm leading-relaxed text-foreground/90 shadow-[var(--shadow-sm)]">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <Separator className="my-10" />

        {/* Comentarios / testimonios */}
        <section data-testid="message-comments-section">
          <div className="mb-4 flex items-center gap-2">
            <MessageCircleHeart className="h-5 w-5 text-primary" />
            <h2 className="font-serif text-2xl font-semibold">Testimonios y palabras de ánimo</h2>
          </div>

          {user ? (
            <form onSubmit={submitComment} className="mb-8">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Comparte cómo este mensaje habló a tu corazón, o deja una palabra de ánimo para otros…"
                className="min-h-[100px] bg-card"
                maxLength={2000}
                data-testid="comment-textarea"
              />
              <div className="mt-3 flex justify-end">
                <Button type="submit" disabled={sending || commentText.trim().length < 2} data-testid="comment-submit-button">
                  {sending ? "Publicando…" : "Compartir testimonio"}
                </Button>
              </div>
            </form>
          ) : (
            <Card className="mb-8 border-border bg-muted/60">
              <CardContent className="flex flex-col items-center gap-3 p-6 text-center" data-testid="comment-login-prompt">
                <p className="text-sm text-muted-foreground">
                  Para compartir un testimonio o palabra de ánimo, necesitas una cuenta. Es gratis y toma un minuto.
                </p>
                <div className="flex gap-3">
                  <Button onClick={() => navigate("/login")} variant="outline" className="bg-card" data-testid="comment-login-button">Iniciar sesión</Button>
                  <Button onClick={() => navigate("/registro")} data-testid="comment-register-button">Crear cuenta</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="comments-empty-state">
              Aún no hay testimonios. Sé el primero/a en compartir una palabra de ánimo.
            </p>
          ) : (
            <div className="space-y-4">
              {comments.map((c) => (
                <Card key={c.id} className="border-border bg-card shadow-[var(--shadow-sm)]" data-testid={`comment-card-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{c.user_name}</span>
                      <span className="flex items-center gap-1.5">
                        {formatFechaHora(c.created_at)}
                        {user && c.user_id !== user.id && (
                          <ReportButton targetType="comment" targetId={c.id} />
                        )}
                        {user && (c.user_id === user.id || user.is_admin) && (
                          <button
                            type="button"
                            title="Eliminar comentario"
                            aria-label="Eliminar comentario"
                            onClick={() => deleteComment(c.id)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid={`comment-delete-${c.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    </div>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">{c.texto}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        <div className="mt-12 text-center">
          <Link to="/archivo" className="text-sm font-semibold text-primary hover:text-primary/80" data-testid="message-back-to-archive">
            ← Volver al archivo
          </Link>
        </div>
      </article>

      {showTop && (
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-4 right-4 z-30 rounded-full bg-card shadow-[var(--shadow-md)]"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="scroll-to-top-button"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
